import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useMemo,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { MeType, emptyMe } from "types/user-types";
import { BridgeMethodResponseType, emptyBridgeMethodsResponse } from "types/mini-app-types";
import { Store, NOTIFICATION_TYPE } from "react-notifications-component";
import PageLoader from "components/page-loader";
import SignIn from "components/auth/sign-in";
import { authorize } from "api/auth";
import { getMeAdmin } from "api/users";
import { getBridgeMethods } from "api/bridge";
import { getApiErrorMessage } from "api/error";

interface SharedState {
  darkMode: boolean;
  setDarkMode: React.Dispatch<React.SetStateAction<boolean>>;
  accessToken: string | null;
  setAccessToken: React.Dispatch<React.SetStateAction<string | null>>;
  refreshToken: string | null;
  setRefreshToken: React.Dispatch<React.SetStateAction<string | null>>;
  login: (phone: string, password: string) => Promise<void>;
  logout: () => void;
  me: MeType;
  isSuperAdmin: boolean;
  bridgeMethods: BridgeMethodResponseType;
  getAndSetBridgeMethods: (page?: number) => Promise<void>;
  notify: (
    message: string,
    type: NOTIFICATION_TYPE,
    title?: string,
    duration?: number
  ) => void;
}

const SharedStateContext = createContext<SharedState | undefined>(undefined);

export function SharedStateProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();

  const [pageLoader, setPageLoader] = useState<boolean>(true);
  const [darkMode, setDarkMode] = useState<boolean>(() => {
    const tablerTheme = localStorage.getItem("tablerTheme");
    return tablerTheme === "dark";
  });
  const [accessToken, setAccessToken] = useState<string | null>(
    localStorage.getItem("accessToken")
  );
  const [refreshToken, setRefreshToken] = useState<string | null>(
    localStorage.getItem("refreshToken")
  );
  const [me, setMe] = useState<MeType>(emptyMe);
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(false);
  const [bridgeMethods, setBridgeMethods] = useState<BridgeMethodResponseType>(
    emptyBridgeMethodsResponse
  );

  const notify = useCallback(
    (
      message: string,
      type: NOTIFICATION_TYPE,
      title?: string,
      duration?: number
    ) => {
      Store.addNotification({
        title,
        message,
        type,
        insert: "top",
        container: "top-left",
        animationIn: ["animate__animated", "animate__bounceIn"],
        animationOut: ["animate__animated", "animate__bounceOut"],
        dismiss: { duration: duration || 3000 },
      });
    },
    []
  );

  const login = async (phone: string, password: string) => {
    try {
      const res = await authorize({ phone, password });
      setAccessToken(res.access.hash);
      setRefreshToken(res.refresh.hash);
      notify("Авторизация прошла успешно", "success");
    } catch (error: unknown) {
      notify(getApiErrorMessage(error, "Ошибка при авторизации"), "danger", "Ошибка при авторизации");
    }
  };

  const logout = () => {
    notify("Деавторизация прошла успешно", "success");
    navigate("/");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    setAccessToken(null);
    setRefreshToken(null);
    setMe(emptyMe);
    setIsSuperAdmin(false);
  };

  const getAndSetMe = useCallback(async () => {
    try {
      const res: MeType = await getMeAdmin();
      setMe(res);
      setIsSuperAdmin(res.user_type === "superadmin");
    } catch (error: unknown) {
      notify(
        getApiErrorMessage(error, "Ошибка при получении профиля"),
        "danger",
        "Ошибка при получении профиля"
      );
    }
  }, [notify]);

  const getAndSetBridgeMethods = useCallback(async (page?: number) => {
    setBridgeMethods(emptyBridgeMethodsResponse);
    try {
      const res: BridgeMethodResponseType = await getBridgeMethods(page);
      setBridgeMethods(res);
    } catch (error: unknown) {
      notify(
        getApiErrorMessage(error, "Ошибка при получении методов бриджа"),
        "danger",
        "Ошибка при получении методов бриджа"
      );
    }
  }, [notify]);

  const initApp = useCallback(async () => {
    setPageLoader(true);
    try {
      await Promise.all([getAndSetMe(), getAndSetBridgeMethods()]);
    } finally {
      setPageLoader(false);
    }
  }, [getAndSetBridgeMethods, getAndSetMe]);

  const value: SharedState = {
    darkMode,
    setDarkMode,
    accessToken,
    setAccessToken,
    refreshToken,
    setRefreshToken,
    login,
    logout,
    me,
    isSuperAdmin,
    bridgeMethods,
    getAndSetBridgeMethods,
    notify,
  };

  const hasToken: boolean = useMemo(() => {
    return Boolean(accessToken || refreshToken);
  }, [accessToken, refreshToken]);

  const DynamicChildren = () => {
    switch (hasToken) {
      case true:
        if (!pageLoader) {
          return children;
        } else {
          return <PageLoader />;
        }

      default:
        return <SignIn />;
    }
  };

  useEffect(() => {
    if (hasToken) initApp();
  }, [hasToken, initApp]);

  useEffect(() => {
    if (darkMode) {
      document.body.setAttribute("data-bs-theme", "dark");
    } else {
      document.body.removeAttribute("data-bs-theme");
    }
  }, [darkMode]);

  return (
    <SharedStateContext.Provider value={value}>
      {DynamicChildren()}
    </SharedStateContext.Provider>
  );
}

export const useSharedState = (): SharedState => {
  const context = useContext(SharedStateContext);
  if (!context) {
    throw new Error("useSharedState must be used within a SharedStateProvider");
  }
  return context;
};
